/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/lookup_submission_statuses-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-lookup_submission_statuses-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/lookup_submission_statuses-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, updated_at
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/lookup_submission_statuses-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-lookup_submission_statuses-api-client.ts (API client)
 * - client/api/generated_schemas/lookup_submission_statuses.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/lookup_submission_statuses-entity-config.ts
 * Public fields: 6/6 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LookupSubmissionStatusesClient, LookupSubmissionStatusesQueryOptions } from '../generated_clients/generated-lookup_submission_statuses-api-client.js';
import {
  LookupSubmissionStatusesInsert,
  LookupSubmissionStatusesUpdate,
  LookupSubmissionStatusesFieldConfig
} from '../../types/generated_schemas/generated-lookup_submission_statuses-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/lookup_submission_statuses_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const lookupSubmissionStatusesKeys = {
  all: ['lookup_submission_statuses'] as const,
  lists: () => [...lookupSubmissionStatusesKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<LookupSubmissionStatusesQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...lookupSubmissionStatusesKeys.lists(), { filters, ...page }] as const,
  details: () => [...lookupSubmissionStatusesKeys.all, 'detail'] as const,
  detail: (id: string) => [...lookupSubmissionStatusesKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...lookupSubmissionStatusesKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...lookupSubmissionStatusesKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...lookupSubmissionStatusesKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...lookupSubmissionStatusesKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useLookupSubmissionStatusesClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new LookupSubmissionStatusesClient();
};

/**
 * Hook to get all lookup_submission_statuses with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with lookup_submission_statuses data
 */
export const useLookupSubmissionStatusesList = (options?: LookupSubmissionStatusesQueryOptions) => {
  const client = useLookupSubmissionStatusesClient();
  
  return useQuery({
    queryKey: lookupSubmissionStatusesKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single lookup_submission_statuse by ID
 *
 * @param id - The lookup_submission_statuse ID
 * @returns React Query result with single lookup_submission_statuse data
 */
export const useLookupSubmissionStatuses = (id: string) => {
  const client = useLookupSubmissionStatusesClient();
  
  return useQuery({
    queryKey: lookupSubmissionStatusesKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/lookup_submission_statuses
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/lookup_submission_statuses_business_hooks/
 */

/**
 * Hook to create a new lookup_submission_statuse
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, updated_at
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating lookup_submission_statuse
 */
export const useCreateLookupSubmissionStatuses = () => {
  const client = useLookupSubmissionStatusesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: LookupSubmissionStatusesInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupSubmissionStatusesKeys.all });
    },
  });
};

/**
 * Hook to update an existing lookup_submission_statuse
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating lookup_submission_statuse
 */
export const useUpdateLookupSubmissionStatuses = () => {
  const client = useLookupSubmissionStatusesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LookupSubmissionStatusesUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: lookupSubmissionStatusesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: lookupSubmissionStatusesKeys.lists() });
    },
  });
};

/**
 * Hook to delete a lookup_submission_statuse
 *
 * @returns React Query mutation for deleting lookup_submission_statuse
 */
export const useDeleteLookupSubmissionStatuses = () => {
  const client = useLookupSubmissionStatusesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupSubmissionStatusesKeys.all });
    },
  });
};

/**
 * Hook to count lookup_submission_statuses with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useLookupSubmissionStatusesCount = (filter?: Partial<Record<typeof LookupSubmissionStatusesFieldConfig.filterable[number], any>>) => {
  const client = useLookupSubmissionStatusesClient();
  
  return useQuery({
    queryKey: lookupSubmissionStatusesKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useLookupSubmissionStatusesFieldConfig = () => {
  return LookupSubmissionStatusesFieldConfig;
};